import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Vertical gap presets for the demand body grid. Change the value passed to
 * <DemandGrid gap="..."> to adjust spacing between every block at once.
 */
const GAP_CLASSES = {
  none: 'gap-y-0',
  sm: 'gap-y-6',
  md: 'gap-y-10',
  lg: 'gap-y-16',
  xl: 'gap-y-40'
} as const;
export type DemandGridGap = keyof typeof GAP_CLASSES;
interface DemandGridProps {
  children: ReactNode;
  /**
   * Vertical spacing between blocks. Defaults to "lg". This is the single knob
   * for tuning the rhythm of the whole page body.
   */
  gap?: DemandGridGap;
  /** Max content width. Defaults to the standard demand body width. */
  className?: string;
}

/**
 * DemandGrid is the single-column layout container for the /demand page body.
 *
 * Every section on the page is a self-contained block dropped in as a direct
 * child. Because the container owns the vertical rhythm (via `gap`), you can
 * reorder blocks freely in the page file without touching per-section margins,
 * and change spacing across the whole page from one place.
 */
export function DemandGrid(t0) {
  const $ = _c(6);
  const {
    children,
    gap: t1,
    className
  } = t0;
  const gap = t1 === undefined ? "lg" : t1;
  const t2 = GAP_CLASSES[gap];
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("grid grid-cols-1", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}

/**
 * DemandBlock wraps a single section so it participates in the DemandGrid
 * rhythm. Use `bleed` for full-width sections (hero, colour strips, CTAs) and
 * the default padded variant for contained content sections.
 */
interface DemandBlockProps {
  children: ReactNode;
  /**
   * When true the block spans the full viewport width with no horizontal
   * padding of its own (the block manages its own internal layout). When
   * false (default) the block is horizontally padded and centred.
   */
  bleed?: boolean;
  id?: string;
  className?: string;
}
export function DemandBlock(t0) {
  const $ = _c(12);
  const {
    children,
    bleed: t1,
    id,
    className
  } = t0;
  const bleed = t1 === undefined ? false : t1;
  if (bleed) {
    let t2;
    if ($[0] !== children || $[1] !== className || $[2] !== id) {
      t2 = <section id={id} className={className}>{children}</section>;
      $[0] = children;
      $[1] = className;
      $[2] = id;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] !== className) {
    t2 = cn("px-5 sm:px-8 lg:px-12", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children) {
    t3 = <div className="mx-auto max-w-[1320px]">{children}</div>;
    $[6] = children;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== id || $[9] !== t2 || $[10] !== t3) {
    t4 = <section id={id} className={t2}>{t3}</section>;
    $[8] = id;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}